import React, { useState, useEffect } from 'react';
import { fetchGoldPrice } from '../services/goldApiService';
import './GoldPriceDisplay.css';

const GoldPriceDisplay = () => {
  const [goldPrices, setGoldPrices] = useState({});
  const [selectedCountry, setSelectedCountry] = useState('INR');
  const [weight, setWeight] = useState(1);
  const [selectedPurity, setSelectedPurity] = useState('24');
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [error, setError] = useState('');

  const purities = [
    { value: '24', label: '24K (99.9% pure)', rate: 1 },
    { value: '22', label: '22K (91.6% pure)', rate: 0.916 },
    { value: '20', label: '20K (83.3% pure)', rate: 0.833 },
    { value: '18', label: '18K (75% pure)', rate: 0.75 },
    { value: '14', label: '14K (58.3% pure)', rate: 0.583 },
    { value: '9', label: '9K (37.5% pure)', rate: 0.375 }
  ];

  const countries = [
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
    { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'THB', name: 'Thai Baht', symbol: '฿' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R' }
  ];

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const data = await fetchGoldPrice(selectedCountry);
        setGoldPrices({
          [selectedCountry]: data
        });
      } catch (error) {
        console.error('Error fetching gold prices:', error);
        setError('Failed to fetch current gold prices. Please try again later.');
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [selectedCountry]);

  useEffect(() => {
    if (goldPrices[selectedCountry]?.price) {
      const selectedPurityData = purities.find(p => p.value === selectedPurity);
      // Convert ozt price to grams (1 ozt = 31.1035 grams)
      const pricePerGram = goldPrices[selectedCountry].price / 31.1035;
      const basePrice = pricePerGram * weight * selectedPurityData.rate;
      setCalculatedPrice(basePrice);
    }
  }, [goldPrices, weight, selectedPurity]);

  const getCurrencySymbol = () => {
    const country = countries.find(c => c.code === selectedCountry);
    return country ? country.symbol : '₹';
  };

  return (
    <div className="gold-calculator-container">
      <div className="live-price-section">
        <h2>Live Gold Price (ozt)</h2>
        <div className="currency-selector">
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
          >
            {countries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <p className="current-price">
          {goldPrices[selectedCountry]?.price 
            ? `${getCurrencySymbol()}${goldPrices[selectedCountry].price.toFixed(2)} per troy ounce` 
            : 'Loading...'}
        </p>
        <p className="price-per-gram">
          {goldPrices[selectedCountry]?.price 
            ? `(${getCurrencySymbol()}${(goldPrices[selectedCountry].price / 31.1035).toFixed(2)} per gram)` 
            : ''}
        </p>
      </div>

      <div className="calculator-section">
        <h2>Gold Calculator</h2>
        
        <div className="calculator-form">
          <div className="form-group">
            <label>1. Choose Your Carat Value</label>
            <div className="purity-buttons">
              {purities.map(purity => (
                <button
                  key={purity.value}
                  className={`purity-btn ${selectedPurity === purity.value ? 'active' : ''}`}
                  onClick={() => setSelectedPurity(purity.value)}
                >
                  {purity.value}K
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>2. Enter the Weight</label>
            <div className="weight-input">
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(parseFloat(e.target.value) || 0)}
                min="0.1"
                step="0.1"
                placeholder="Enter weight in grams"
              />
              <span>Grams</span>
            </div>
          </div>
        </div>

        <div className="result-section">
          <h3>Approximate Price</h3>
          <p className="calculated-price">
            {getCurrencySymbol()}{calculatedPrice.toFixed(2)}
          </p>
          <p className="price-details">
            {selectedPurity}K Gold ({weight}g)
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default GoldPriceDisplay; 